import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { environment as env } from '../../environments/environment';
import { Product } from '../models/product.model';
import { NotificationsService } from '../services/notifications.service';
import { Package } from './../models/package.model';
import { OrdersApiService } from './orders-api.service';
import { ProductApiService } from './product-api.service';
import { ReplaySubject } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService implements OnDestroy {

  private subscriptions = new Map<string, Subscription>();

  $valid: boolean;
  isValid: BehaviorSubject<boolean> = new BehaviorSubject(false);
  $pricesChanged: ReplaySubject<boolean> = new ReplaySubject(1);
  pricesChanged$ = this.$pricesChanged.pipe(startWith(false));
  $certificatesChanged: ReplaySubject<boolean> = new ReplaySubject(1);
  certificatesChanged$ = this.$certificatesChanged.pipe(startWith(false));
  itemAdded: Subject<Package> = new Subject();
  itemRemoved: Subject<Package> = new Subject();
  itemsChanged: Subject<Package[]> = new Subject();
  items: ReplaySubject<Package[]> = new ReplaySubject(1);
  itemsTotalsChanged: BehaviorSubject<any> = new BehaviorSubject({});
  itemsAsProducts: BehaviorSubject<Product[]> = new BehaviorSubject([]);
  cleared: Subject<boolean> = new Subject();
  private $products: Product[];
  public $packages: Package[] = [];
  totals: any;

  public inputPrices: Record<string, number> = {};
  public inputCerts: Record<string, string> = {};

  constructor(
    private notification: NotificationsService,
    private productApiService: ProductApiService,
    private ordersApiService: OrdersApiService,
  ) {
    // subscribe to products changes
    this.subscriptions.set('productApiUpdate',
      this.productApiService.products.asObservable().subscribe(data => {
        // load shopping cart from local storage when we get the batch of packages
        if (data.length) {
          this.restoreFromLocalStorage();
          this.itemsChanged.next(this.$packages.slice());
        }
      })
    );

    // store and recalculate totals when contents change
    this.subscriptions.set('itemsChanged',
      this.itemsChanged.subscribe(() => {
        this.items.next(this.$packages.slice());
        this.updateProducts();
        // this.verifyPackages();
        // this.saveToLocalStorage();
        this.updateTotals();
      })
    );

    // clear cart when the order is sent
    this.subscriptions.set('ordersApiOrderSent',
      this.ordersApiService.orderSent.subscribe(() => this.clear())
    );

    // run once to initialize cart summary on init
    this.updateTotals();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private restoreFromLocalStorage() {
    try {
      const storedPackages = JSON.parse(window.localStorage.getItem('orderSelectedPackages')) || [];
      const storedProducts = JSON.parse(window.localStorage.getItem('orderSelectedProducts')) || [];
      // tslint:disable-next-line:max-line-length
      // console.log('ShoppingCartService: Restoring shopping cart from LocalStorage (%s packages in %s products).', storedPackages.length, storedProducts.length);
      if (storedPackages && storedPackages.length > 0) {
        this.$packages = storedPackages;
        // this.itemsChanged.next(this.$packages.slice());
      }
      if (storedProducts && storedProducts.length > 0) {
        this.$products = storedProducts;
        this.updateProducts();
      }
    } catch (e) {
      console.error('ShoppingCartService: Exception while restoring contents from LocalStorage.', e);
    }
  }

  private saveToLocalStorage() {
    window.localStorage.setItem('orderSelectedPackages', JSON.stringify(this.$packages));
    window.localStorage.setItem('orderSelectedProducts', JSON.stringify(this.$products));
  }

  private getIndexOf(pkg: Package): number {
    return this.$packages.findIndex(p => p.id === pkg.id);
  }

  private get(pkg: Package): Package {
    return this.$packages.find(p => p.id === pkg.id);
  }

  /**
   * Get cart contents.
   */

  contents(): Package[] {
    return this.$packages.slice();
  }

  /**
   * Check if a Package is in the shopping cart
   */

  has(pack: Package): boolean {
    return this.getIndexOf(pack) > -1;
  }

  /**
   * Check if a Package with a specific ID is in the shopping cart
   */

  hasId(id: number): boolean {
    return this.$packages.findIndex(pack => pack.id === id) > -1;
  }

  /**
   * Add a package to or remove it from (if it already exists) the shopping cart
   */

  toggle(pack: Package) {
    if (this.has(pack)) {
      this.remove(pack);
    } else {
      this.add(pack);
    }
  }

  /**
   * Add a Package to the shopping cart
   */

  add(pack: Package) {
    this.$packages.push(Object.assign({}, pack));
    this.itemAdded.next(Object.assign({}, pack));
    this.itemsChanged.next(this.$packages.slice());
    this.updateProducts();
  }


  /**
   * Remove a Package from the shopping cart
   *
  */

  remove(pack: Package): boolean {
    const index = this.getIndexOf(pack);
    if (index > -1) {
      this.$packages.splice(index, 1);
    }
    this.itemRemoved.next(pack);
    this.itemsChanged.next(this.$packages.slice());
    this.updateProducts();
    return index > -1;
  }

  /**
   * Remove all Packages not matching supplied array from the shopping cart
   *
  */

  discardOthers(packs: Package[]): void {
    const ids = packs.map(pack => pack.id);
    let removed = 0;
    this.$packages = this.$packages.filter(pack => {
      const match = ids.indexOf(pack.id) > -1;
      removed += match ? 0 : 1;
      return match;
    });
    // this.$packages = this.$packages.filter(pack => lostPackages.findIndex(p => p.id === pack.id) > -1);
    if (removed > 0) {
      this.notification.send('NOTIFICATIONS.TEXT_PACKAGES_NO_LONGER_AVAILABLE');
      // this.itemsChanged.next(this.$packages.slice());
    }
  }

  /**
   * Get a statistics object with the contents of the cart.
   */
  getTotals(): any {
    return this.totals;
  }

  /**
   * Update sums and totals of the cart contents
   */
  private updateTotals(): void {

    // an object with interesting numbers to display in the cart summary
    this.totals = this.$packages.reduce((totals: any, pack: Package) => {
      totals.weight += +(pack.netWeight ? pack.netWeight.value : 0);
      totals.volume += +(pack.volume ? pack.volume.value : 0);
      return totals;
    }, { weight: 0, volume: 0, volumeMax: 15, weightMax: 28000, weightPercent: 0, volumeRemaining: 0 });

    this.totals.weightPercent = Math.ceil(this.totals.weight * 100 / this.totals.weightMax);
    // next one is how many pixels to fill the truck (40 is max height)
    const maxTruckHeight = 32;
    this.totals.weightPercentTruck = Math.min(maxTruckHeight, Math.ceil(this.totals.weight * maxTruckHeight / this.totals.weightMax));
    this.totals.weight = Math.ceil(this.totals.weight);
    this.totals.volumeRemaining = +(this.totals.volumeMax - this.totals.volume).toFixed(3);
    this.totals.volume = +(this.totals.volume).toFixed(3);

    // send any warnings and update status
    if (this.totals.weightPercent > 100) {
      this.$valid = false;
      this.notification.send('NOTIFICATIONS.TEXT_TOO_MUCH_WEIGHT');
    } else if (this.totals.weightPercent < (env.requiredTruckFillPercent || 3)) {
      this.$valid = false;
    } else {
      this.$valid = true;
    }
    this.isValid.next(this.$valid);

    this.itemsTotalsChanged.next(this.totals);
  }

  /**
   * Make a complete product list with the packages in the cart.
   *
   */
  private updateProducts(): void {
    const availablePackages = [];
    this.$products = this.productApiService.get().reduce((list, product) => {
      // we need to create new objects here, or the package references will be modified later
      const prod = JSON.parse(JSON.stringify(product));
      // replace with the packages from the cart
      const packages = prod.packages.filter(pkg => this.has(pkg)).map(pkg => this.get(pkg));
      if (packages.length > 0) {
        availablePackages.push(...packages.slice());
        list.push(Object.assign({}, prod, { packages }));
      }
      return list;
    }, []);
    this.discardOthers(availablePackages);
    this.itemsAsProducts.next(this.$products.slice());
    this.saveToLocalStorage();
  }

  /**
   * Get the product list representation of the cart.
   */
  getProducts() {
    return this.$products.slice();
  }


  /**
   * Clear the cart to prepare for a new order
   */

  clear() {
    this.$packages = [];
    this.$products = [];
    this.saveToLocalStorage();
    this.itemsChanged.next(this.$packages.slice());
    this.itemsAsProducts.next(this.$products.slice());
    this.updateTotals();
    this.cleared.next(true);
  }

  /**
   * Handle user input for price and certs
   */

  setPriceCert(pkg: Package, price: number, cert: string) {
    const index = this.getIndexOf(pkg);
    this.setPrice(pkg, price);
    this.setCert(pkg, cert);
    this.updateProducts();
  }

  setPriceAndUpdate(pkg: Package, price: number) {
    console.log('setPriceAndUpdate', pkg, price);
    this.setPrice(pkg, price);
    this.updateProducts();
  }

  setPrice(pkg: Package, price: number) {
    for (const pack of this.$packages) {
      if (pack.id === pkg.id || (pack.productCode === pkg.productCode && !pack.selectedPrice)) {
        console.log('pack set price', pack);
        pack.selectedPrice = price;
      }
    }
    this.$pricesChanged.next(true);
  }

  setCertAndUpdate(pkg: Package, cert: string) {
    this.setCert(pkg, cert);
    this.updateProducts();
  }

  setCert(pkg: Package, cert: string) {
    for (const pack of this.$packages) {
      if (pack.id === pkg.id || (!pack.certificateCode && !pack.selectedCert)) {
        pack.selectedCert = cert;
      }
    }
    this.$certificatesChanged.next(true);
  }

  getPrice(pkg: Package) {
    return this.get(pkg)?.selectedPrice;
  }

  getCert(pkg: Package) {
    return this.get(pkg)?.selectedCert;
  }

}
